import React, { useState, useEffect, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Box, Typography, CircularProgress, Alert, Button } from "@mui/material";
import ArrowBackIosIcon from "@mui/icons-material/ArrowBackIos";
import RolePermissionsTable from "./RolePermissionsTable";
import { Heading } from "../../../../components/styledConstants";
// Reusable Modals
import FeedbackModal from "../../PermissionManagement/PermissionComponents/FeedbackModal";

// API functions
import { fetchRolePermissionsRequest, updateRolePermissionsRequest } from "../../../../utils/api";

const PermissionsPage = () => {
  const { roleName } = useParams();
  const navigate = useNavigate();

  const [allPermissions, setAllPermissions] = useState([]);
  const [currentRolePermissions, setCurrentRolePermissions] = useState([]);
  const [selectedPermissions, setSelectedPermissions] = useState(new Set());

  const [loading, setLoading] = useState(true);
  const [updating, setUpdating] = useState(false);
  const [error, setError] = useState(null);

  const [feedbackModal, setFeedbackModal] = useState({
    open: false,
    title: "",
    message: "",
    type: "success",
  });

  const handleFailure = useCallback((title, err) => {
    const apiError = err.response?.data?.msg || err.message || `Failed to ${title.toLowerCase()}`;
    setError(apiError);
    setFeedbackModal({
      open: true,
      title: "Error",
      message: apiError,
      type: "error",
    });
  }, []);

  const handleSuccess = useCallback((title, message) => {
    setFeedbackModal({
      open: true,
      title: title,
      message: message,
      type: "success",
    });
  }, []);

  const fetchPermissionsData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await fetchRolePermissionsRequest(roleName);
      console.log(response, "role permissions response");
      if (response.data.code === "0000") {
        const totalPermissions = response.data.data.totalPermissions;
        const assignedPermissions = new Set(response.data.data.assignedPermission.map((p) => p.name));
        const assigned = totalPermissions.filter((p) => assignedPermissions.has(p.name));
        const unassigned = totalPermissions.filter((p) => !assignedPermissions.has(p.name));
        const sortedAssigned = assigned.sort((a, b) => a.name.localeCompare(b.name));
        const sortedUnassigned = unassigned.sort((a, b) => a.name.localeCompare(b.name));

        // Combine the sorted lists, with assigned first
        const combinedPermissions = [...sortedAssigned, ...sortedUnassigned];
        setAllPermissions(combinedPermissions);
        setCurrentRolePermissions(response.data.data.assignedPermission);
        setSelectedPermissions(assignedPermissions);
      } else {
        const errorMessage = response.data.msg || "Failed to fetch permissions.";
        handleFailure("fetch permissions", { response: { data: { msg: errorMessage } } });
      }
    } catch (err) {
      handleFailure("fetch permissions", err);
    } finally {
      setLoading(false);
    }
  }, [roleName, handleFailure]);

  useEffect(() => {
    fetchPermissionsData();
  }, [fetchPermissionsData]);

  const handleUpdatePermissions = async () => {
    setUpdating(true);
    try {
      const assignedPermissionsNames = new Set(currentRolePermissions.map((p) => p.name));
      const addPermissions = [...selectedPermissions].filter((p) => !assignedPermissionsNames.has(p));
      const removePermissions = [...assignedPermissionsNames].filter((p) => !selectedPermissions.has(p));

      const response = await updateRolePermissionsRequest(roleName, addPermissions, removePermissions);

      if (response.data.code === "0000") {
        const successMessage = response.data.metaInfo?.msg || "Role permissions updated successfully.";
        handleSuccess("Success", successMessage);
        await fetchPermissionsData(); // Re-fetch data to show the updated state
      } else {
        handleFailure("update role permissions", {
          response: { data: { msg: response.data.msg } },
        });
      }
    } catch (err) {
      handleFailure("update role permissions", err);
    } finally {
      setUpdating(false);
    }
  };

  const handleCloseFeedbackModal = () => {
    setFeedbackModal({ ...feedbackModal, open: false });
    if (feedbackModal.type === "success") {
      navigate("/role-management");
    }
  };

  const handlePermissionToggle = (permissionName) => {
    setSelectedPermissions((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(permissionName)) {
        newSet.delete(permissionName);
      } else {
        newSet.add(permissionName);
      }
      return newSet;
    });
  };

  if (loading) {
    return (
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          height: "100vh",
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  return (
    <>
      <Heading size="xxl">Permissions for Role: {roleName}</Heading>
      <Box
        sx={{
          p: 2,
          maxWidth: 1200,
          margin: "auto",
          bgcolor: "background.paper",
          borderRadius: 2,
          boxShadow: 3,
          marginTop: 2,
        }}
      >
        {error && (
          <Alert severity="error" sx={{ my: 2 }}>
            {error}
          </Alert>
        )}

        <RolePermissionsTable
          permissions={allPermissions}
          selectedPermissions={selectedPermissions}
          onPermissionToggle={handlePermissionToggle}
          roleName={roleName}
          onSave={handleUpdatePermissions}
          updating={updating}
        />
      </Box>

      <FeedbackModal
        open={feedbackModal.open}
        title={feedbackModal.title}
        message={feedbackModal.message}
        onClose={handleCloseFeedbackModal}
        type={feedbackModal.type}
      />
    </>
  );
};

export default PermissionsPage;
